import { useCallback, useMemo, useState } from "react"
import { Col } from "react-bootstrap"
import { Button } from "@inmagik/magik-ui"
import { v4 as uuidv4 } from "uuid"
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable"
import { ModalBlock } from "./components/ModalBlock"
import { SortableBlockCard } from "./components/SortableBlockCard"
import type { BlockBase, Path } from "../../types"
import { BlockCollapsedHint, BlockPreview } from "./components/Previews"

export default function PathEditorForm({
  col = 6,
  currentValue,
  onSave,
  path,
}: {
  col?: number
  currentValue: any
  onSave: (values: any) => void
  path?: Path
}) {
  const [currentBlock, setCurrentBlock] = useState<BlockBase | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [insertIndex, setInsertIndex] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const editBlock = useCallback(
    (block: BlockBase) => {
      const idx = (currentValue || []).findIndex((b: BlockBase) => b.uuid === block.uuid)
      if (idx !== -1) {
        const next = [...currentValue]
        next[idx] = block
        onSave(next)
      } else {
        const next = [...(currentValue || [])]
        // If we're adding a new block and we have an insertion target, insert there.
        if (insertIndex !== null && insertIndex >= 0 && insertIndex <= next.length) {
          next.splice(insertIndex, 0, block)
        } else {
          next.push(block)
        }
        onSave(next)
      }
    },
    [currentValue, insertIndex, onSave],
  )

  const addBlockAt = useCallback((index: number | null) => {
    setInsertIndex(index)
    setCurrentBlock({ uuid: uuidv4(), blockType: "" } as BlockBase)
  }, [])

  const addBlock = useCallback(() => {
    // default: append
    addBlockAt((currentValue || []).length)
  }, [addBlockAt, currentValue])

  const clearInsertIndex = useCallback(() => setInsertIndex(null), [])

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

  const ids = useMemo(() => (currentValue || []).map((b: BlockBase) => b.uuid), [currentValue])

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      setIsDragging(false)
      const { active, over } = event
      if (!over || active.id === over.id) return

      const oldIndex = (currentValue || []).findIndex((b: BlockBase) => b.uuid === active.id)
      const newIndex = (currentValue || []).findIndex((b: BlockBase) => b.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      onSave(arrayMove(currentValue, oldIndex, newIndex))
    },
    [currentValue, onSave],
  )

  const urlFrontPercorso = useMemo(() => {
    if (!path?.id) return ""

    const isLocal = window.location.hostname === "localhost"

    const baseUrl = isLocal ? "http://localhost:3000" : "https://cabiria.inmagik.com"

    return `${baseUrl}/temi-e-percorsi/${path.id}/percorso`
  }, [path?.id])

  return (
    <Col xs={col} className="d-flex flex-column align-items-stretch gap-4">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={() => setIsDragging(true)}
        onDragCancel={() => setIsDragging(false)}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          <div>
            {/* Insert before the first card */}
            {!isDragging && <BetweenAddButton onAdd={() => addBlockAt(0)} label="Aggiungi sopra" />}

            {(currentValue || []).map((block: BlockBase, index: number) => (
              <div key={block.uuid}>
                <SortableBlockCard
                  orderIndex={index + 1}
                  block={block}
                  onEdit={(b) => {
                    clearInsertIndex()
                    setCurrentBlock(b)
                  }}
                  onDelete={(uuid) => {
                    if (expandedId === uuid) setExpandedId(null)
                    onSave((currentValue || []).filter((b: BlockBase) => b.uuid !== uuid))
                  }}
                  expanded={expandedId === block.uuid}
                  onToggle={() => setExpandedId((prev) => (prev === block.uuid ? null : block.uuid))}
                  collapsedHint={<BlockCollapsedHint block={block} />}
                  preview={<BlockPreview block={block} />}
                />

                {/* Insert between this card and the next (i.e. at index + 1) */}
                {!isDragging && (
                  <BetweenAddButton
                    onAdd={() => addBlockAt(index + 1)}
                    label={index === (currentValue || []).length - 1 ? "Aggiungi sotto" : "Aggiungi qui"}
                  />
                )}
              </div>
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <div className="position-fixed" style={{ bottom: 24, right: 24 }}>
        <Button
          size="md"
          outline
          title="Visualizza preview"
          disabled={!urlFrontPercorso}
          onClick={() => {
            if (!urlFrontPercorso) return
            window.open(urlFrontPercorso, "_blank")
          }}
          leadingIcon="visibility"
          style={{ width: "fit-content" }}
          className="me-2"
        />

        <Button
          size="md"
          title="Aggiungi Blocco"
          onClick={addBlock}
          leadingIcon="add"
          style={{ width: "fit-content" }}
        />
      </div>
      {currentBlock && (
        <ModalBlock
          block={currentBlock}
          setCurrentBlock={setCurrentBlock}
          onClose={() => {
            clearInsertIndex()
            setCurrentBlock(null)
          }}
          editBlock={editBlock}
        />
      )}
    </Col>
  )
}

function BetweenAddButton({ onAdd, label }: { onAdd: () => void; label: string }) {
  return (
    <div className="d-flex align-items-center justify-content-center between-add" style={{ padding: "10px 0" }}>
      {/* Hover styles: show the button only when hovering the separator */}
      <style>{`
        .between-add { width: 100%; position: relative; }
        .between-add__wrap { width: 100%; position: relative; display: flex; align-items: center; justify-content: center; }
        .between-add__btn {
          opacity: 0;
          transform: translateY(2px) scale(0.98);
          pointer-events: none;
          transition: opacity 140ms ease, transform 140ms ease;
        }
        .between-add:hover .between-add__btn,
        .between-add__btn:focus-visible {
          opacity: 1;
          transform: translateY(0) scale(1);
          pointer-events: auto;
        }
      `}</style>

      <div className="between-add__wrap">
        <div
          style={{
            height: 1,
            background: "rgba(0,0,0,.08)",
            position: "absolute",
            left: 0,
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
        <Button
          size="sm"
          title={label}
          onClick={(e: any) => {
            e?.stopPropagation?.()
            // After click the button would remain visible due to focus; blur it.
            e?.currentTarget?.blur?.()
            onAdd()
          }}
          leadingIcon="add"
          className="between-add__btn"
          style={{
            position: "relative",
            zIndex: 1,
            width: "fit-content",
            marginInline: "auto",
            borderRadius: 999,
            background: "#fff",
            color: "#0d6efd",
            boxShadow: "0 6px 18px rgba(0,0,0,.06)",
          }}
        />
      </div>
    </div>
  )
}
